import { db } from '../firebase';
import { doc, getDoc, collection, getDocs, query, where, limit, setDoc, deleteDoc } from 'firebase/firestore';

// Collection name for API keys
const API_KEYS_COLLECTION = 'api_keys';

/**
 * Get the OpenAI API key from Firestore
 * This is a centralized API key that can be used by all users
 * @returns {Promise<string|null>} The API key or null if not found
 */
export const getOpenAIApiKey = async () => {
  try {
    // First try to get the key from the api_keys collection
    const apiKeysRef = collection(db, API_KEYS_COLLECTION);
    const q = query(apiKeysRef, where('provider', '==', 'openai'), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const keyDoc = querySnapshot.docs[0];
      const keyData = keyDoc.data();
      
      if (keyData.apiKey) {
        console.log('Found OpenAI API key in api_keys collection');
        return keyData.apiKey;
      }
    }
    
    // If not found, try the app_settings collection as fallback
    const settingsRef = doc(db, 'app_settings', 'openai');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      const settingsData = settingsDoc.data();
      
      if (settingsData.apiKey) {
        console.log('Found OpenAI API key in app_settings collection');
        return settingsData.apiKey;
      }
    }
    
    console.log('No OpenAI API key found in Firestore');
    return null;
  } catch (error) {
    console.error('Error getting OpenAI API key:', error);
    return null;
  }
};

/**
 * Get the API key for a specific provider from Firestore
 * @param {string} provider - The provider name (openai, deepseek, gemini)
 * @returns {Promise<string|null>} The API key or null if not found
 */
export const getProviderApiKey = async (provider) => {
  try {
    // Validate provider
    if (!provider) {
      console.error('Provider name is required');
      return null;
    }
    
    // Normalize provider name
    const normalizedProvider = provider.toLowerCase();
    
    // First try to get the key from the api_keys collection
    const apiKeysRef = collection(db, API_KEYS_COLLECTION);
    const q = query(apiKeysRef, where('provider', '==', normalizedProvider), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const keyDoc = querySnapshot.docs[0];
      const keyData = keyDoc.data();
      
      if (keyData.apiKey) {
        console.log(`Found ${normalizedProvider} API key in api_keys collection`);
        return keyData.apiKey;
      }
    }
    
    // If not found, try the app_settings collection as fallback
    const settingsRef = doc(db, 'app_settings', normalizedProvider);
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      const settingsData = settingsDoc.data();
      
      if (settingsData.apiKey) {
        console.log(`Found ${normalizedProvider} API key in app_settings collection`);
        return settingsData.apiKey;
      }
    }
    
    console.log(`No ${normalizedProvider} API key found in Firestore`);
    return null;
  } catch (error) {
    console.error(`Error getting ${provider} API key:`, error);
    return null;
  }
};

/**
 * Save an API key for a specific provider to Firestore
 * @param {string} provider - The provider name (openai, deepseek, gemini)
 * @param {string} apiKey - The API key
 * @param {string} baseUrl - Optional base URL for the API
 * @returns {Promise<boolean>} Success status
 */
export const saveProviderApiKey = async (provider, apiKey, baseUrl = null) => {
  try {
    // Validate inputs
    if (!provider) {
      console.error('Provider name is required');
      return false;
    }
    
    if (!apiKey) {
      console.error('API key is required');
      return false;
    }
    
    // Normalize provider name
    const normalizedProvider = provider.toLowerCase();
    
    // Create the document data
    const keyData = {
      provider: normalizedProvider,
      apiKey,
      updatedAt: new Date().toISOString()
    };
    
    // Add baseUrl if provided
    if (baseUrl) {
      keyData.baseUrl = baseUrl;
    }
    
    // Save to the api_keys collection
    const keyRef = doc(db, API_KEYS_COLLECTION, normalizedProvider);
    await setDoc(keyRef, keyData);
    
    console.log(`Saved ${normalizedProvider} API key to Firestore`);
    return true;
  } catch (error) {
    console.error(`Error saving ${provider} API key:`, error);
    return false;
  }
};

/**
 * Delete an API key for a specific provider from Firestore
 * @param {string} provider - The provider name (openai, deepseek, gemini)
 * @returns {Promise<boolean>} Success status
 */
export const deleteProviderApiKey = async (provider) => {
  try {
    // Validate inputs
    if (!provider) {
      console.error('Provider name is required');
      return false;
    }
    
    // Normalize provider name
    const normalizedProvider = provider.toLowerCase();
    
    // Delete from the api_keys collection
    const keyRef = doc(db, API_KEYS_COLLECTION, normalizedProvider);
    await deleteDoc(keyRef);
    
    console.log(`Deleted ${normalizedProvider} API key from Firestore`);
    return true;
  } catch (error) {
    console.error(`Error deleting ${provider} API key:`, error);
    return false;
  }
};
